"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { rankSearchCandidatesStatic } from "@/lib/blog/search-candidates-browser";
import { normalizeBlogSearchText } from "@/lib/blog/search-lexical";
import { BLOG_SEARCH_MIN_QUERY_LENGTH } from "@/lib/blog/search-constants";
import type { LiveSuggestionRequestError } from "@/app/components/live-blog-suggestions";

export const MAX_TYPEAHEAD_ITEMS = 8;
export const LIVE_SUGGESTION_DEBOUNCE_MS = 180;
export const MIN_LIVE_QUERY_LENGTH = BLOG_SEARCH_MIN_QUERY_LENGTH;
const LIVE_SUGGESTION_REQUEST_TIMEOUT_MS = 6_000;
const LIVE_SUGGESTION_TIMEOUT_RETRY_DELAY_MS = 1_200;
const MAX_TIMEOUT_RETRIES_PER_QUERY = 1;

export type LiveSuggestionStatus =
  | "idle"
  | "loading"
  | "success"
  | "error"
  | "rate_limited";

export function buildPreparedSuggestions(
  suggestions: readonly string[]
): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const entry of suggestions) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const normalized = normalizeBlogSearchText(trimmed);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    unique.push(trimmed);
  }
  return unique;
}

export function buildStaticFilteredSuggestions({
  inputValue,
  preparedSuggestions,
  maxItems = MAX_TYPEAHEAD_ITEMS,
}: {
  inputValue: string;
  preparedSuggestions: readonly string[];
  maxItems?: number;
}): string[] {
  return rankSearchCandidatesStatic({
    candidates: preparedSuggestions.map((suggestion, index) => ({
      id: `${index}`,
      index,
      label: suggestion,
      meta: suggestion,
    })),
    maxItems,
    query: inputValue,
  }).map((entry) => entry.candidate.meta);
}

export function buildSuggestionStatusMessage({
  liveSuggestionStatus,
  normalizedInputValueLength,
  minQueryLength = MIN_LIVE_QUERY_LENGTH,
  hasVisibleSuggestions,
  activeSuggestionIndex,
  filteredSuggestions,
}: {
  liveSuggestionStatus: LiveSuggestionStatus;
  normalizedInputValueLength: number;
  minQueryLength?: number;
  hasVisibleSuggestions: boolean;
  activeSuggestionIndex: number;
  filteredSuggestions: readonly string[];
}): string {
  if (
    liveSuggestionStatus === "loading" &&
    normalizedInputValueLength >= minQueryLength
  ) {
    return "Loading suggestions.";
  }
  if (
    liveSuggestionStatus === "error" &&
    normalizedInputValueLength >= minQueryLength &&
    filteredSuggestions.length === 0
  ) {
    return "Suggestions are temporarily unavailable.";
  }
  if (
    liveSuggestionStatus === "rate_limited" &&
    normalizedInputValueLength >= minQueryLength &&
    filteredSuggestions.length === 0
  ) {
    return "Suggestions are temporarily rate limited. Please wait a moment.";
  }
  if (
    liveSuggestionStatus === "success" &&
    normalizedInputValueLength >= minQueryLength &&
    filteredSuggestions.length === 0
  ) {
    return "No suggestions found.";
  }
  if (!hasVisibleSuggestions) return "";

  if (activeSuggestionIndex >= 0) {
    const activeSuggestion = filteredSuggestions[activeSuggestionIndex];
    if (activeSuggestion) {
      return `Suggestion ${activeSuggestionIndex + 1} of ${
        filteredSuggestions.length
      }: ${activeSuggestion}.`;
    }
  }

  return `${filteredSuggestions.length} suggestions available. Use up and down arrow keys to navigate and Enter to select.`;
}

export function useSearchTypeahead({
  inputValue,
  suggestions,
  isComposing,
  fetchSuggestions,
  maxItems = MAX_TYPEAHEAD_ITEMS,
  minQueryLength = MIN_LIVE_QUERY_LENGTH,
  debounceMs = LIVE_SUGGESTION_DEBOUNCE_MS,
}: {
  inputValue: string;
  suggestions: readonly string[];
  isComposing: boolean;
  fetchSuggestions: (args: {
    query: string;
    limit: number;
    signal: AbortSignal;
  }) => Promise<string[]>;
  maxItems?: number;
  minQueryLength?: number;
  debounceMs?: number;
}): {
  preparedSuggestions: string[];
  staticFilteredSuggestions: string[];
  filteredSuggestions: string[];
  normalizedInputValue: string;
  liveSuggestions: string[];
  liveSuggestionStatus: LiveSuggestionStatus;
  usesLiveSuggestions: boolean;
  suggestionBadgeLabel: "Live" | "Suggestion";
} {
  const liveSuggestionDebounceRef = useRef<number | null>(null);
  const liveSuggestionAbortRef = useRef<AbortController | null>(null);
  const liveSuggestionTimeoutRef = useRef<number | null>(null);
  const liveSuggestionRetryTimeoutRef = useRef<number | null>(null);
  const liveSuggestionRateLimitTimeoutRef = useRef<number | null>(null);
  const liveSuggestionRetryCountRef = useRef(0);
  const liveSuggestionRetryQueryRef = useRef("");
  const liveSuggestionRequestIdRef = useRef(0);
  const liveSuggestionRateLimitUntilRef = useRef(0);
  const [liveSuggestionRetryNonce, setLiveSuggestionRetryNonce] = useState(0);
  const [liveSuggestions, setLiveSuggestions] = useState<string[]>([]);
  const [liveSuggestionStatus, setLiveSuggestionStatus] =
    useState<LiveSuggestionStatus>("idle");

  const preparedSuggestions = useMemo(
    () => buildPreparedSuggestions(suggestions),
    [suggestions]
  );

  const staticFilteredSuggestions = useMemo(
    () =>
      buildStaticFilteredSuggestions({
        inputValue,
        preparedSuggestions,
        maxItems,
      }),
    [inputValue, maxItems, preparedSuggestions]
  );

  const normalizedInputValue = useMemo(
    () => normalizeBlogSearchText(inputValue),
    [inputValue]
  );

  useEffect(
    () => () => {
      if (liveSuggestionDebounceRef.current !== null) {
        window.clearTimeout(liveSuggestionDebounceRef.current);
      }
      if (liveSuggestionAbortRef.current) {
        liveSuggestionAbortRef.current.abort();
      }
      if (liveSuggestionTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionTimeoutRef.current);
      }
      if (liveSuggestionRetryTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionRetryTimeoutRef.current);
      }
      if (liveSuggestionRateLimitTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionRateLimitTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (liveSuggestionDebounceRef.current !== null) {
      window.clearTimeout(liveSuggestionDebounceRef.current);
      liveSuggestionDebounceRef.current = null;
    }
    if (liveSuggestionAbortRef.current) {
      liveSuggestionAbortRef.current.abort();
      liveSuggestionAbortRef.current = null;
    }
    if (liveSuggestionTimeoutRef.current !== null) {
      window.clearTimeout(liveSuggestionTimeoutRef.current);
      liveSuggestionTimeoutRef.current = null;
    }
    if (liveSuggestionRetryTimeoutRef.current !== null) {
      window.clearTimeout(liveSuggestionRetryTimeoutRef.current);
      liveSuggestionRetryTimeoutRef.current = null;
    }
    if (liveSuggestionRateLimitTimeoutRef.current !== null) {
      window.clearTimeout(liveSuggestionRateLimitTimeoutRef.current);
      liveSuggestionRateLimitTimeoutRef.current = null;
    }

    if (liveSuggestionRetryQueryRef.current !== normalizedInputValue) {
      liveSuggestionRetryQueryRef.current = normalizedInputValue;
      liveSuggestionRetryCountRef.current = 0;
    }

    if (isComposing) {
      return;
    }

    if (normalizedInputValue.length < minQueryLength) {
      setLiveSuggestionStatus("idle");
      setLiveSuggestions([]);
      return;
    }

    const now = Date.now();
    if (now < liveSuggestionRateLimitUntilRef.current) {
      const retryAfterMs = liveSuggestionRateLimitUntilRef.current - now;
      setLiveSuggestionStatus("rate_limited");
      setLiveSuggestions([]);
      liveSuggestionRateLimitTimeoutRef.current = window.setTimeout(() => {
        liveSuggestionRateLimitTimeoutRef.current = null;
        setLiveSuggestionRetryNonce((previous) => previous + 1);
      }, retryAfterMs);
      return;
    }

    setLiveSuggestionStatus("loading");
    const requestId = liveSuggestionRequestIdRef.current + 1;
    liveSuggestionRequestIdRef.current = requestId;

    liveSuggestionDebounceRef.current = window.setTimeout(async () => {
      const controller = new AbortController();
      liveSuggestionAbortRef.current = controller;
      let requestTimedOut = false;
      const timeoutId = window.setTimeout(() => {
        requestTimedOut = true;
        if (liveSuggestionTimeoutRef.current === timeoutId) {
          liveSuggestionTimeoutRef.current = null;
        }
        controller.abort();
      }, LIVE_SUGGESTION_REQUEST_TIMEOUT_MS);
      liveSuggestionTimeoutRef.current = timeoutId;

      try {
        const suggestionsFromApi = await fetchSuggestions({
          query: normalizedInputValue,
          limit: maxItems,
          signal: controller.signal,
        });
        if (requestId !== liveSuggestionRequestIdRef.current) return;
        setLiveSuggestions(suggestionsFromApi);
        setLiveSuggestionStatus("success");
        liveSuggestionRetryCountRef.current = 0;
      } catch (error) {
        if (requestId !== liveSuggestionRequestIdRef.current) return;
        if (controller.signal.aborted) {
          if (requestTimedOut) {
            setLiveSuggestions([]);
            setLiveSuggestionStatus("error");
            if (
              liveSuggestionRetryCountRef.current < MAX_TIMEOUT_RETRIES_PER_QUERY
            ) {
              liveSuggestionRetryCountRef.current += 1;
              liveSuggestionRetryTimeoutRef.current = window.setTimeout(() => {
                liveSuggestionRetryTimeoutRef.current = null;
                if (requestId !== liveSuggestionRequestIdRef.current) return;
                setLiveSuggestionRetryNonce((previous) => previous + 1);
              }, LIVE_SUGGESTION_TIMEOUT_RETRY_DELAY_MS);
            }
          }
          return;
        }

        const maybeRequestError = error as LiveSuggestionRequestError;
        if (maybeRequestError?.status === 429) {
          const retryAfterMs = (maybeRequestError.retryAfterSec ?? 10) * 1000;
          liveSuggestionRateLimitUntilRef.current = Date.now() + retryAfterMs;
          setLiveSuggestions([]);
          setLiveSuggestionStatus("rate_limited");
          if (liveSuggestionRateLimitTimeoutRef.current !== null) {
            window.clearTimeout(liveSuggestionRateLimitTimeoutRef.current);
          }
          liveSuggestionRateLimitTimeoutRef.current = window.setTimeout(() => {
            liveSuggestionRateLimitTimeoutRef.current = null;
            setLiveSuggestionRetryNonce((previous) => previous + 1);
          }, retryAfterMs);
          return;
        }

        setLiveSuggestions([]);
        setLiveSuggestionStatus("error");
      } finally {
        if (liveSuggestionTimeoutRef.current === timeoutId) {
          window.clearTimeout(timeoutId);
          liveSuggestionTimeoutRef.current = null;
        }
        if (liveSuggestionAbortRef.current === controller) {
          liveSuggestionAbortRef.current = null;
        }
      }
    }, debounceMs);

    return () => {
      if (liveSuggestionDebounceRef.current !== null) {
        window.clearTimeout(liveSuggestionDebounceRef.current);
        liveSuggestionDebounceRef.current = null;
      }
      if (liveSuggestionAbortRef.current) {
        liveSuggestionAbortRef.current.abort();
        liveSuggestionAbortRef.current = null;
      }
      if (liveSuggestionTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionTimeoutRef.current);
        liveSuggestionTimeoutRef.current = null;
      }
      if (liveSuggestionRetryTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionRetryTimeoutRef.current);
        liveSuggestionRetryTimeoutRef.current = null;
      }
      if (liveSuggestionRateLimitTimeoutRef.current !== null) {
        window.clearTimeout(liveSuggestionRateLimitTimeoutRef.current);
        liveSuggestionRateLimitTimeoutRef.current = null;
      }
    };
  }, [
    debounceMs,
    fetchSuggestions,
    isComposing,
    maxItems,
    minQueryLength,
    normalizedInputValue,
    liveSuggestionRetryNonce,
  ]);

  const hasLiveSuggestionResponse =
    normalizedInputValue.length >= minQueryLength &&
    liveSuggestionStatus === "success";
  const usesLiveSuggestions =
    hasLiveSuggestionResponse && liveSuggestions.length > 0;

  const filteredSuggestions = useMemo(() => {
    if (hasLiveSuggestionResponse) return liveSuggestions;
    return staticFilteredSuggestions;
  }, [hasLiveSuggestionResponse, liveSuggestions, staticFilteredSuggestions]);

  return {
    preparedSuggestions,
    staticFilteredSuggestions,
    filteredSuggestions,
    normalizedInputValue,
    liveSuggestions,
    liveSuggestionStatus,
    usesLiveSuggestions,
    suggestionBadgeLabel: hasLiveSuggestionResponse ? "Live" : "Suggestion",
  };
}
